import type { SamlProviderValue } from '@funkey/api/src/features/auth/ory-polis/shared';
import type { UserTenantStatus } from '@funkey/api/src/database/client/schema/user/tenant.schema';
import { Data } from 'effect';

export declare namespace Bizz_LoginMethod {
    export type SAML = {
        readonly bizz: {
            readonly slug: string;
            readonly name: string;
            /** `bizz/logo` media path (a full URL), null when the customer uploaded none. */
            readonly logo: string | null;
        };
        /** True when the tenant was matched on the email domain rather than a membership: we don't
         * know this person, so we must not assume SSO is their only way in. */
        readonly inferredFromEmailDomain: boolean;
        /** One entry per configured IdP. Empty when the tenant has SSO on but no connection rows yet —
         * the login link then falls back to polis' IdP-selection screen. */
        readonly connections: readonly {
            readonly id: number;
            /** The IdP's own entityID — what the sign-in button names the provider from. */
            readonly idpEntityId: string | null;
            /** Only consulted when `idpEntityId` is null (rows from the 0012 backfill). */
            readonly provider: SamlProviderValue;
            readonly label: string | null;
        }[];
    };
}
/** Consumer mail providers: their users share a domain but no employer, so the domain says nothing
 * about which company someone belongs to. Never used to infer a tenant. */
export const BIZZ_LOGIN_PERSONAL_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
    'gmail.com',
    'googlemail.com',
    'hotmail.com',
    'hotmail.be',
    'outlook.com',
    'outlook.be',
    'live.com',
    'live.be',
    'msn.com',
    'yahoo.com',
    'yahoo.co.uk',
    'icloud.com',
    'me.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
    'gmx.com',
    'gmx.net',
    'telenet.be',
    'skynet.be',
    'scarlet.be',
    'voo.be',
    'edpnet.be',
    'free.fr',
    'orange.fr',
    'wanadoo.fr',
    'web.de',
    't-online.de',
]);

/** Which sign-in routes a person's memberships open up, before any IdP lookup.
 *
 * Split out because the two answers key on the membership status differently, and reading one filter
 * as if it answered both is what locked SCIM-provisioned users out of SSO entirely. */
export function bizz_resolveMemberships<
    T extends {
        readonly tenantId: number;
        readonly enableSaml: boolean;
        readonly enableCredentials: boolean;
        readonly status: (typeof UserTenantStatus.enumValues)[number];
    },
>(rows: readonly T[]) {
    return {
        /** A password belongs to someone who has finished onboarding — nobody else. */
        hasCredentials: rows.some(row => row.enableCredentials && row.status === 'ACTIVE'),
        /** Any membership names the tenant, whatever its status: a SCIM directory writes the row
         * *before* the first login, so an `INVITED` member is someone we know, not a stranger.
         * `DISABLED` is revoked access and never names a tenant to sign in to.
         *
         * Some people hold several — consultants working for two customers, Funkey staff attached to
         * every bizz they support. The query has no `ORDER BY`, so picking the first row hands them a
         * different company on different requests. Refusing to choose is worse still: they'd fall to
         * the domain fallback, which rejects an ambiguous domain, and end up with no way in at all.
         * So sort: a membership they already use beats one they haven't, then the oldest tenant. */
        ssoTenant:
            rows
                .filter(row => row.enableSaml && row.status !== 'DISABLED')
                .sort(
                    (a, b) =>
                        onboardedFirst(a.status) - onboardedFirst(b.status) ||
                        a.tenantId - b.tenantId,
                )
                .at(0) ?? null,
    };
}

function onboardedFirst(status: (typeof UserTenantStatus.enumValues)[number]): number {
    return status === 'ACTIVE' ? 0 : 1;
}

export type Bizz_LoginMethod = Data.TaggedEnum<{
    credentials: {};
    saml: Bizz_LoginMethod.SAML;
}>;

export const Bizz_LoginMethod = Data.taggedEnum<Bizz_LoginMethod>();
